/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationGroupMemberProps {
  id: string;
  name: string;
  userId: string | null;
  notificationAddress: string | null;
  notificationGroupId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationGroupMemberRecord extends Record<NotificationGroupMemberProps>({
  id: null as any,
  name: null as any,
  userId: null as any,
  notificationAddress: null as any,
  notificationGroupId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    userId: string | null;
    notificationAddress: string | null;
    notificationGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationGroupMemberRecord {
    if (json == null) {
      throw new Error('NotificationGroupMemberRecord.fromJS: parameter cannot be null');
    }
    return new NotificationGroupMemberRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationGroupMemberJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NotificationGroupMemberJson.name'),
      userId: json.userId,
      notificationAddress: json.notificationAddress,
      notificationGroupId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
        'NotificationGroupMemberJson.notificationGroupId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'NotificationGroupMemberJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    userId: string | null;
    notificationAddress: string | null;
    notificationGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationGroupMemberRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationGroupMemberRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationGroupMemberRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationGroupMemberJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NotificationGroupMemberJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userId: tryOrDefault(
        () => json.userId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationGroupId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
            'NotificationGroupMemberJson.notificationGroupId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'NotificationGroupMemberJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationGroupMemberProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationGroupMemberRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationGroupMemberViewModelBase implements NotificationGroupMemberProps {
  protected readonly _props: NotificationGroupMemberProps;
  private _json?: NotificationGroupMemberProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get userId(): string | null {
    return this._props.userId;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get notificationGroupId(): string {
    return this._props.notificationGroupId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: NotificationGroupMemberProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        userId: this._props.userId,
        notificationAddress: this._props.notificationAddress,
        notificationGroupId: this._props.notificationGroupId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationGroupMemberEntities = (state: AppState) =>
  state.entityStores.notificationGroupMember.entities;

export const selectNotificationGroupMemberById = (id: string) => (state: AppState) =>
  state.entityStores.notificationGroupMember.entities.get(id);

export const selectNotificationGroupMemberMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notificationGroupMember.meta.get(id);

export const selectNotificationGroupMemberAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationGroupMemberById(id),
    selectNotificationGroupMemberMetadataById(id),
    (notificationGroupMember, meta) => ({ notificationGroupMember, meta }),
  );

export function selectNotificationGroupMemberEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationGroupMemberEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationGroupMemberRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationGroupMemberEntitiesByName(name: string) {
  return createSelector(selectAllNotificationGroupMemberEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNotificationGroupMemberEntitiesByUserId(userId: string) {
  return createSelector(selectAllNotificationGroupMemberEntities, (entities) =>
    entities.filter((e) => e.userId === userId).valueSeq(),
  );
}

export function selectNotificationGroupMemberEntitiesByNotificationAddress(
  notificationAddress: string,
) {
  return createSelector(selectAllNotificationGroupMemberEntities, (entities) =>
    entities.filter((e) => e.notificationAddress === notificationAddress).valueSeq(),
  );
}

export function selectNotificationGroupMemberEntitiesByNotificationGroupId(
  notificationGroupId: string,
) {
  return createSelector(selectAllNotificationGroupMemberEntities, (entities) =>
    entities.filter((e) => e.notificationGroupId === notificationGroupId).valueSeq(),
  );
}

export function selectNotificationGroupMemberEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationGroupMemberEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
